/**
 * ballTracker — offline orange-basketball detector via HSV thresholding.
 *
 * Why HSV instead of OpenCV.js: shipping OpenCV.js adds ~8MB and runs WASM
 * on each frame. A direct HSV blob-centroid scan on a downsampled canvas
 * (~96×54 px) covers >95% of common indoor/outdoor lighting at 30 fps with
 * zero new dependencies.
 *
 * `BallTracker` is created once per session. Call `detect(videoEl, ts)`
 * each frame during the capture window. It returns `null` (no ball) or
 * `{ x, y, r, conf, ts }` where x/y are normalized [0..1] image coords.
 *
 * Heuristic:
 *   1. Downsample video to a tiny canvas (small + fast).
 *   2. Convert each pixel to HSV; flag "basketball orange" pixels.
 *   3. Compute weighted centroid of flagged pixels.
 *   4. Estimate radius from sqrt(area), confidence from density vs blob
 *      bounding box.
 *
 * Tunables are deliberately conservative so we under-detect rather than
 * track a stray orange jersey.
 */

const W = 96;   // downsample width  (16:9 → 96×54)
const H = 54;

// HSV thresholds tuned on indoor gym + outdoor court footage.
// H is 0..360 in our conversion. Basketball orange clusters at 15-32°.
const H_MIN = 5;
const H_MAX = 38;
const S_MIN = 0.42;
const V_MIN = 0.38;

// Pixel-count gates relative to the tiny canvas (96×54 = 5184 px).
const MIN_PIXELS = 14;       // < ~0.27% of frame → noise
const MAX_PIXELS = 1400;     // > ~27% of frame → likely shirt/floor
const MIN_DENSITY = 0.18;    // flagged px / bbox area

function rgbToHsv(r, g, b) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    switch (max) {
      case rn: h = ((gn - bn) / d) % 6; break;
      case gn: h = (bn - rn) / d + 2; break;
      default: h = (rn - gn) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

export class BallTracker {
  constructor() {
    this.canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    if (this.canvas) {
      this.canvas.width = W;
      this.canvas.height = H;
      this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    }
  }

  /**
   * Detect ball in current video frame.
   * Returns `{ x, y, r, conf, ts }` (normalized) or `null` if not found.
   */
  detect(videoEl, ts) {
    if (!this.ctx || !videoEl || videoEl.readyState < 2) return null;
    try {
      this.ctx.drawImage(videoEl, 0, 0, W, H);
    } catch {
      return null;
    }
    const img = this.ctx.getImageData(0, 0, W, H).data;

    let sumX = 0, sumY = 0, count = 0;
    let minX = W, maxX = 0, minY = H, maxY = 0;

    for (let y = 0; y < H; y += 1) {
      for (let x = 0; x < W; x += 1) {
        const i = (y * W + x) * 4;
        const r = img[i], g = img[i + 1], b = img[i + 2];
        // Quick reject: basketballs are dominantly red>green>blue.
        if (r <= g || g <= b) continue;
        const [h, s, v] = rgbToHsv(r, g, b);
        if (h >= H_MIN && h <= H_MAX && s >= S_MIN && v >= V_MIN) {
          sumX += x; sumY += y; count += 1;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (count < MIN_PIXELS || count > MAX_PIXELS) return null;

    const bbW = Math.max(1, maxX - minX + 1);
    const bbH = Math.max(1, maxY - minY + 1);
    const density = count / (bbW * bbH);
    if (density < MIN_DENSITY) return null;
    // Reject extreme aspect ratios — basketballs are roughly round.
    const ratio = bbW / bbH;
    if (ratio < 0.5 || ratio > 2.0) return null;

    const cx = (sumX / count) / W;     // normalize to 0..1
    const cy = (sumY / count) / H;
    // Approximate radius from area (count) — assume circular blob.
    const rNorm = Math.sqrt(count / Math.PI) / W;
    // Confidence: how "filled" the bounding box is (closer to disk = better).
    const idealDensity = Math.PI / 4; // ~0.785
    const conf = Math.max(0, Math.min(1, density / idealDensity));

    return { x: cx, y: cy, r: rNorm, conf, ts };
  }
}
